import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { canManageCommunity } from '../utils/permissions';
import { useActivities } from '../context/ActivitiesContext';

export const useCommunity = (communityId) => {
  const { user } = useAuth();
  const { communities, updateCommunityInList } = useActivities();
  const [community, setCommunity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCommunity = useCallback(async () => {
    if (!communityId) return;
    try {
      setLoading(true);
      const data = await api.get(`/communities/${communityId}`);
      setCommunity(data);
      if (updateCommunityInList) {
        updateCommunityInList(data);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to fetch community', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [communityId, updateCommunityInList]);

  useEffect(() => {
    // Try to find in context first
    const existing = communities?.find(c => String(c.id) === String(communityId) || c.slug === communityId);
    if (existing) {
      setCommunity(existing);
      setLoading(false);
    } else {
      fetchCommunity();
    }
  }, [communityId, communities, fetchCommunity]);

  const isAdmin = canManageCommunity(user, communityId, community);

  const joinCommunity = async (password = null) => {
    try {
      const res = await api.post(`/communities/${communityId}/join`, { password });
      await fetchCommunity();
      return res;
    } catch (err) {
      console.error('Failed to join community', err);
      throw err;
    }
  };

  const checkPassword = async (password) => {
    try {
      await api.post(`/communities/${communityId}/unlock`, { password });
      return true;
    } catch (err) {
      return false;
    }
  };

  const leaveCommunity = async () => {
    try {
      const res = await api.post(`/communities/${communityId}/leave`);
      await fetchCommunity();
      return res;
    } catch (err) {
      console.error('Failed to leave community', err);
      throw err;
    }
  };

  return {
    community,
    loading,
    error,
    isAdmin,
    joinCommunity,
    leaveCommunity,
    checkPassword,
    refreshCommunity: fetchCommunity
  };
};
